// El filtro de Producto de Creaciones, dentro del selector de creaciones de un
// lanzamiento — ADR-2026-10-20-filtro-de-producto-en-el-selector-de-creaciones.
//
// Misma lista (`lib/productosDelFiltro`), mismo criterio de marca y mismo dibujo que
// el desplegable de `GeneratedImagesHistory`: lo que la persona aprendió allá le sirve
// acá. Elige UNA variante; qué se hace con ella (pedir sus creaciones) es del selector.

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Image as ImageIcon, RotateCw, Search, Shapes } from 'lucide-react';
import {
  buscarEnProductos,
  fetchProductosDelFiltro,
  firmarMiniaturasDeProductos,
  productosDeMarca,
  type ProductoDelFiltro,
} from '../../lib/productosDelFiltro';

export function FiltroDeProducto({
  companyId,
  brandId,
  valor,
  onCambiar,
}: {
  companyId: string | null;
  /** La marca del lanzamiento: la lista se acota a ella (y a los productos sin marca). */
  brandId: string | null;
  /** El id de la variante elegida; `null` = todos los productos. */
  valor: string | null;
  onCambiar: (varianteId: string | null) => void;
}) {
  const [productos, setProductos] = useState<ProductoDelFiltro[]>([]);
  /** La lista no se pudo leer: se dice y se ofrece reintentar, no "sin productos". */
  const [fallo, setFallo] = useState(false);
  const [intento, setIntento] = useState(0);
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [miniaturas, setMiniaturas] = useState<Record<string, string>>({});
  const pedidas = useRef<Set<string>>(new Set());
  const caja = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!companyId) return;
    let vivo = true;
    setFallo(false);
    fetchProductosDelFiltro(companyId).then(
      (r) => {
        if (vivo) setProductos(r);
      },
      (e) => {
        console.error('[FiltroDeProducto] productos', e);
        if (vivo) setFallo(true);
      },
    );
    return () => {
      vivo = false;
    };
  }, [companyId, intento]);

  const deLaMarca = productosDeMarca(productos, brandId);
  const visibles = buscarEnProductos(deLaMarca, busqueda);
  const elegido = valor !== null ? productos.find((p) => String(p.id) === valor) : undefined;

  // Firmar las miniaturas recién cuando la lista se abre (y de nuevo al buscar,
  // que trae productos que estaban más abajo).
  useEffect(() => {
    if (!abierto || !companyId) return;
    void firmarMiniaturasDeProductos(companyId, visibles, pedidas.current).then((nuevas) => {
      if (Object.keys(nuevas).length) setMiniaturas((prev) => ({ ...prev, ...nuevas }));
    });
    // `visibles` es un array nuevo por pintado: lo que cambia la lista es esto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, busqueda, productos, companyId]);

  // La miniatura del botón cuando se elige sin haber abierto la lista (no pasa hoy,
  // pero el botón no puede depender de eso).
  useEffect(() => {
    if (!elegido || !companyId) return;
    void firmarMiniaturasDeProductos(companyId, [elegido], pedidas.current).then((nuevas) => {
      if (Object.keys(nuevas).length) setMiniaturas((prev) => ({ ...prev, ...nuevas }));
    });
  }, [elegido, companyId]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const elegir = (id: string | null) => {
    onCambiar(id);
    setAbierto(false);
    setBusqueda('');
  };

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        onClick={() => {
          setAbierto(!abierto);
          setBusqueda('');
        }}
        title="Ver todas las creaciones de un producto"
        className={`inline-flex h-10 max-w-full items-center justify-between gap-2 rounded-md border px-4 text-sm font-medium shadow-sm outline-none transition-colors sm:max-w-[280px] ${
          valor !== null
            ? 'border-brand-primary bg-brand-primary text-white hover:bg-brand-primary-hover'
            : 'border-neutral-200 bg-surface-card text-neutral-900 hover:bg-surface-elevated'
        }`}
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          {elegido && miniaturas[String(elegido.id)] && (
            <img
              src={miniaturas[String(elegido.id)]}
              alt=""
              className="h-5 w-5 shrink-0 rounded-[4px] bg-white object-cover"
            />
          )}
          <span className="truncate">{elegido?.nombre ?? 'Todos los productos'}</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${valor !== null ? 'text-white/70' : 'text-neutral-400'} ${
            abierto ? 'rotate-180' : ''
          }`}
        />
      </button>
      {abierto && (
        <div className="absolute z-50 mt-1 w-[280px] max-w-[calc(100vw-3rem)] animate-fade-in rounded-md border border-neutral-200 bg-surface-card py-1 shadow-md">
          {/* Buscador: el catálogo puede tener cientos de productos y una
              lista scrolleada sola no alcanza para encontrar uno. */}
          <div className="px-2 pb-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                autoFocus
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar producto..."
                className="w-full rounded-md border border-neutral-200 bg-surface-card py-1.5 pl-8 pr-2 text-[13px] text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-brand-primary"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => elegir(null)}
            className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors ${
              valor === null ? 'bg-neutral-100 font-medium text-neutral-900' : 'text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] border border-neutral-200 bg-neutral-100">
              <Shapes className="h-4 w-4 text-neutral-400" />
            </span>
            Todos los productos
          </button>
          <div className="scrollbar-thin max-h-64 overflow-y-auto">
            {visibles.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => elegir(String(v.id))}
                className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors ${
                  String(v.id) === valor
                    ? 'bg-neutral-100 font-medium text-neutral-900'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                {/* Sin foto queda la caja gris con el ícono, para que la lista no baile. */}
                {miniaturas[String(v.id)] ? (
                  <img
                    src={miniaturas[String(v.id)]}
                    alt=""
                    loading="lazy"
                    className="h-8 w-8 shrink-0 rounded-[6px] border border-neutral-200 bg-neutral-100 object-cover"
                  />
                ) : (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] border border-neutral-200 bg-neutral-100">
                    <ImageIcon className="h-3.5 w-3.5 text-neutral-300" />
                  </span>
                )}
                <span className="truncate">{v.nombre}</span>
                {String(v.id) === valor && <Check className="ml-auto h-4 w-4 shrink-0 text-brand-primary" />}
              </button>
            ))}
            {fallo ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-neutral-500">
                No se pudieron cargar los productos.
                <button
                  type="button"
                  onClick={() => setIntento((n) => n + 1)}
                  className="inline-flex shrink-0 items-center gap-1 font-medium text-brand-primary hover:underline"
                >
                  <RotateCw className="h-3.5 w-3.5" />
                  Reintentar
                </button>
              </div>
            ) : (
              visibles.length === 0 && (
                <p className="px-3 py-2 text-[13px] text-neutral-400">Sin productos que coincidan</p>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
